import { EditorConfig } from "@ckeditor/ckeditor5-core";
import { BalloonEditor } from "@ckeditor/ckeditor5-editor-balloon";
import {
  Alignment,
  Autoformat,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  Heading,
  Highlight,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  SimpleUploadAdapter,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableToolbar,
  TodoList,
  Underline,
  WordCount,
  Mention,
} from "./ckeditor-plugins";

export const LICENSE_KEY = "GPL";

// Define plugins as a separate array (not modifying BalloonEditor globally)
export const BALLOON_EDITOR_PLUGINS = [
  Alignment,
  Autoformat,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  Heading,
  Highlight,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  SimpleUploadAdapter,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableToolbar,
  TodoList,
  Underline,
  WordCount,
  Mention,
];

// Define default configuration as a separate object
export const BALLOON_EDITOR_CONFIG: EditorConfig = {
  // Balloon editor specific toolbar configuration
  toolbar: {
    items: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "link",
      "highlight",
      "|",
      "bulletedList",
      "numberedList",
      "todoList",
      "|",
      "blockQuote",
      "insertTable",
      "|",
      "undo",
      "redo",
    ],
    shouldNotGroupWhenFull: true, // Better for balloon editor
  },

  // Balloon specific configuration
  balloonToolbar: [
    "bold",
    "italic",
    "underline",
    "|",
    "link",
    "highlight",
    "|",
    "bulletedList",
    "numberedList",
    "|",
    "outdent",
    "indent",
  ],

  blockToolbar: [
    "paragraph",
    "heading1",
    "heading2",
    "heading3",
    "|",
    "bulletedList",
    "numberedList",
    "todoList",
    "|",
    "blockQuote",
    "insertTable",
  ],

  fontFamily: {
    supportAllValues: true,
  },

  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 22],
    supportAllValues: true,
  },

  heading: {
    options: [
      {
        model: "paragraph",
        title: "Paragraph",
        class: "ck-heading_paragraph",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      {
        model: "heading5",
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5",
      },
      {
        model: "heading6",
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6",
      },
    ],
  },

  licenseKey: LICENSE_KEY,

  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },

  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties",
    ],
  },

  // Mention configuration
  mention: {
    feeds: [
      {
        marker: "@",
        feed: [], // Should be provided by consumer
        minimumCharacters: 1,
      },
    ],
  },
};

// Create a custom editor class WITHOUT modifying the original BalloonEditor
export default class CustomBalloonEditor extends BalloonEditor {
  // Define builtin plugins for this specific editor instance
  public static override builtinPlugins = BALLOON_EDITOR_PLUGINS;

  // Define default config for this specific editor instance
  public static override defaultConfig = BALLOON_EDITOR_CONFIG;

  // Optional: Add a static method to create editor with specific config
  public static override async create(
    sourceElementOrData: HTMLElement | string,
    config?: EditorConfig
  ): Promise<CustomBalloonEditor> {
    // Merge provided config with default config
    const mergedConfig: EditorConfig = {
      ...BALLOON_EDITOR_CONFIG,
      ...config,
      // Ensure plugins are properly merged
      plugins: config?.plugins || BALLOON_EDITOR_PLUGINS,
      // Merge balloon-specific configurations
      balloonToolbar:
        config?.balloonToolbar || BALLOON_EDITOR_CONFIG.balloonToolbar,
      blockToolbar: config?.blockToolbar || BALLOON_EDITOR_CONFIG.blockToolbar,
    };

    return super.create(sourceElementOrData, mergedConfig) as Promise<CustomBalloonEditor>;
  }
}

// Export both the class and the configuration separately for flexibility
export {
  CustomBalloonEditor,
  BALLOON_EDITOR_CONFIG as BalloonEditorConfig,
  BALLOON_EDITOR_PLUGINS as BalloonEditorPlugins,
};

// Also export a convenience function to create the editor
export const createBalloonEditor = async (
  element: HTMLElement | string,
  customConfig?: Partial<EditorConfig>
): Promise<CustomBalloonEditor> => {
  return CustomBalloonEditor.create(element, customConfig);
};

// Export legacy class name for backward compatibility
export { CustomBalloonEditor as CustomEditor };
